import { QP_CALENDAR_VIEW_MODE, TTF } from '@/constants/queryParams';
import { ROUTE_CALENDAR_LIST } from '@/constants/routes';
import useAuth from '@/hooks/useAuth';
import { useSearchParams } from '@/hooks/useSearchParams';
import { useCreateCalendarEventMutation, useListCalendarEvents } from '@/queries/calendar-event.queries';
import { useGetCalendarQuery, useRemoveCalendarMutation } from '@/queries/calendar.queries';
import uuidService from '@/services/uuid.service';
import { createInstant } from '@/timetime/timetime';
import { ICalendarEvent } from '@/types/calendar-event/ICalendarEvent';
import { useToast } from '@chakra-ui/react';
import { t } from 'i18next';
import { useHistory } from 'react-router-dom';
import { Temporal } from 'temporal-polyfill';

export const VISIBLE_DAYS = 5;

const QP_EDIT_CALENDAR = 'editCalendar';

export function useBookingPlannerController() {
	const toast = useToast();
	const history = useHistory();
	const auth = useAuth();
	const params = useSearchParams();
	const from = params.get(TTF) || Temporal.Now.plainDateISO().toString();
	const fromInstant = createInstant({ timestamp: from, timezone: auth.getCurrentUser().timeZone });
	const calendarIds = params.get('ids');
	const hidePending = params.get('hidePending');
	const createCalendarEventMutation = useCreateCalendarEventMutation();
	const removeCalendarMutation = useRemoveCalendarMutation();

	if (!calendarIds) {
		throw Error('Missing calendar IDs');
	}

	// TODO: In the future we need to allow multipl
	const calendarId = calendarIds?.split(',')[0] as string;

	const calendarQuery = useGetCalendarQuery({ id: calendarId });

	const fromDate = Temporal.PlainDate.from(from).toZonedDateTime({ timeZone: auth.getCurrentUser().timeZone });
	const timeMin = fromDate.with({ hour: 0, minute: 0, second: 0, millisecond: 0 }).toInstant().toString();
	const timeMax = fromDate
		.add({ days: VISIBLE_DAYS })
		.with({ hour: 0, minute: 0, second: 0, millisecond: 0 })
		.toInstant()
		.toString();

	const calendarEventsQuery = useListCalendarEvents({
		calendarIds: calendarIds,
		includeEventsWithoutInterval: true,
		timeMin,
		timeMax,
	});

	function handleEventClick(calendarEvent: ICalendarEvent) {
		params.set('event-id', calendarEvent.id);
		params.set('calendar-id', calendarEvent.calendarId);
		history.replace({ search: params.toString() });
	}

	function handleCloseClick() {
		params.delete('event-id');
		history.push({ search: params.toString() });
	}

	function handleEditCalendarCloseClick() {
		params.delete(QP_EDIT_CALENDAR);
		history.replace({ search: params.toString() });
	}

	function handleNextClick() {
		const tomorrow = Temporal.PlainDate.from(from).add({ days: 1 });
		params.set(TTF, tomorrow.toString());
		history.push({ search: params.toString() });
	}

	function handlePrevClick() {
		const yesterday = Temporal.PlainDate.from(from).subtract({ days: 1 });
		params.set(TTF, yesterday.toString());
		history.push({ search: params.toString() });
	}

	function handleCreateEventClick() {
		// TODO: Allow to have multiple calendars
		console.debug('Events are created on the first calendar!');
		const id = uuidService.v4();

		return createCalendarEventMutation
			.mutateAsync({
				calendarId,
				id,
				summary: t('New event'),
			})
			.then(() => {
				params.set('calendar-id', calendarId);
				params.set('event-id', id);
				history.push({ search: params.toString() });
			});
	}

	function handleMonthClick() {
		params.set(QP_CALENDAR_VIEW_MODE, 'month');
		history.push({ search: params.toString() });
	}

	function handleShowPendingClick() {
		hidePending ? params.delete('hidePending') : params.set('hidePending', 'true');
		history.replace({ search: params.toString() });
	}

	function handleEditCalendarClick() {
		params.set(QP_EDIT_CALENDAR, calendarId);
		history.replace({ search: params.toString() });
	}

	function handleRemoveCalendarClick() {
		return removeCalendarMutation
			.mutateAsync({
				id: calendarId,
			})
			.then(() => {
				toast({
					status: 'success',
					title: t('Calendar deleted'),
				});
				history.push(ROUTE_CALENDAR_LIST);
			});
	}

	return {
		handleRemoveCalendarClick,
		handleCloseClick,
		handleCreateEventClick,
		handleEventClick,
		handleMonthClick,
		handleNextClick,
		handlePrevClick,
		handleShowPendingClick,
		handleEditCalendarCloseClick,
		handleEditCalendarClick,
		calendarEventsQuery,
		calendarQuery,
		events: calendarEventsQuery.data?.items?.filter((e) => !!e.interval) ?? [],
		fromInstant,
		hidePending: !!hidePending,
		isModalOpen: !!params.get('event-id'),
		isEditCalendarModalOpen: !!params.get(QP_EDIT_CALENDAR),
		pendingEvents: calendarEventsQuery.data?.items?.filter((e) => !e.interval) ?? [],
	};
}
